/********************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2024
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} **/

import jsyaml from 'js-yaml';

export function cleanUpKey(key) {
  return key ? key.toString().replace(/\W/g, '') : '';
}

export function objectToArray(obj) {
  return Object.keys(obj).reduce((accumulator, key) => {
    if (key.indexOf('$$') !== -1) {
      return accumulator;
    }

    accumulator.push({
      key,
      value: obj[key]
    });

    return accumulator;
  }, []);
}

export function renderSchema(schema, unexpandedSchema) {
  // empty string if no schema given
  if (!schema) {
    return '';
  }
  // if it's an array, return 'array' -
  // the details of the item type are more interesting
  if (schema.type === 'array') {
    return 'array';
  }
  // if the schema has been expanded, the most useful
  // type information is in the $ref
  if (unexpandedSchema && unexpandedSchema.$ref) {
    return unexpandedSchema.$ref.split('/').pop();
  }
  // otherwise, we might have an inline schema
  if (schema.type) {
    return schema.type;
  }
  // or perhaps we still have a reference?
  if (schema.$ref) {
    return schema.$ref.split('/').pop();
  }
  // ok, we have no idea
  return '';
}

export function renderType(parameter) {
  if (!parameter) {
    return '';
  }
  if (parameter.type) {
    return parameter.type;
  }
  if (!parameter.schema) {
    return '';
  }
  if (parameter.schema.type) {
    return parameter.schema.type;
  }
  if (!parameter.schema.$ref) {
    return '';
  }
  return parameter.schema.$ref.split('/').pop();
}

export function filterOperations(operations, view, query) {
  if (!query || !operations) {
    return operations;
  }
  query = query.toLowerCase();
  return operations.filter(function (operation) {
    if ((view === 'summary' || view === 'tags_summary') && operation.summary && operation.summary.toLowerCase().indexOf(query) >= 0) {
      return true;
    }

    if (view === 'operations' && operation.operationId && operation.operationId.toLowerCase().indexOf(query) >= 0) {
      return true;
    }

    if (operation.method && operation.method.toLowerCase().indexOf(query) >= 0) {
      return true;
    }

    if (operation.path && operation.path.toLowerCase().indexOf(query) >= 0) {
      return true;
    }

    return false;
  });
}

export function filterWebhooks(webhooks, query) {
  if (!query || !webhooks) {
    return webhooks;
  }

  query = query.toLowerCase();

  return webhooks.filter(function (webhook) {
    if (webhook.event && webhook.event.toLowerCase().indexOf(query) >= 0) {
      return true;
    }

    if (webhook.method && webhook.method.toLowerCase().indexOf(query) >= 0) {
      return true;
    }
    return false;
  });
}

export function filterTags(tags, view, query) {
  if (!query || !tags) {
    return tags;
  }

  query = query.toLowerCase();
  let filteredTags = JSON.parse(JSON.stringify(tags));
  let tagEmpty = function (tag) {
    if (tag.operations && tag.operations.length > 0) {
      return false;
    }

    if (tag.tags && Object.keys(tag.tags).length > 0) {
      return false;
    }

    return true;
  };

  let filterTag = function (tag) {
    if (tag.tags) {
      Object.keys(tag.tags).forEach(function (tagName) {
        tag.tags[tagName] = filterTag(tag.tags[tagName]);
        if (tagEmpty(tag.tags[tagName])) {
          delete tag.tags[tagName];
        }
      });
    }

    if (tag.operations) {
      tag.operations = filterOperations(tag.operations, view, query);
    }

    return tag;
  };

  Object.keys(filteredTags).forEach(function (tagName) {
    filteredTags[tagName] = filterTag(filteredTags[tagName]);
    if (tagEmpty(filteredTags[tagName])) {
      delete filteredTags[tagName];
    }
  });

  return filteredTags;
}

export function filterChannels(channels = {}, query = '') {
  query = query.toLowerCase();
  return channels.filter(function (channel) {
    return channel.id().toLowerCase().indexOf(query) >= 0;
  });
}

export function filterDefinitions(definitionNames, query) {
  if (!query || !definitionNames) {
    return definitionNames;
  }

  query = query.toLowerCase();
  return definitionNames.filter(function (definitionName) {
    return definitionName.toLowerCase().indexOf(query) >= 0;
  });
}

let cleanAmp = new RegExp('&', 'ig');
let cleanLt = new RegExp('<', 'ig');
export function highlightCleaner(input, matching) {
  if (!input) {
    return;
  }

  input = input.toString().replace(cleanAmp, '&amp;');
  input = input.toString().replace(cleanLt, '&lt;');
  if (!input || !matching || matching === '') {
    return input;
  }

  let reg = new RegExp(matching, 'ig');
  return input.toString().replace(reg, function (match, $1, $2, offset, original) {
    return '<span class="ui-match" data-testid="highlight-query">' + match + '</span>';
  });
}

export function generateRandomTag(tagName) {
  let asInt = 0;
  let a = 0;
  let tagCol = ['red', 'magenta', 'purple', 'blue', 'cyan', 'teal', 'green', 'gray', 'cool-gray', 'warm-gray'];
  for (a = 0; a < tagName.length; a++) {
    asInt += tagName.charCodeAt(a);
  }
  return 'cds--tag--' + tagCol[asInt % 7];
}

export function b64DecodeUnicode(str) {
  // Going backwards: from bytestream, to percent-encoding, to original string.
  return decodeURIComponent(
    atob(str)
      .split('')
      .map(function (c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join('')
  );
}

export function isExpandable(text, maxLines = 8) {
  if (text && typeof text === 'string' && text.match) {
    let matches = text.match(/\n/g);
    if (matches && matches.length > maxLines) {
      return true;
    }
  }
  return false;
}

export function isIE() {
  return !!navigator.msSaveBlob;
}

export function isWindows() {
  return navigator.platform.match(/Win/) !== null;
}

export const generateNameOfApi = (api) => {
  if (api && api.info && api.info.title && api.info.version) {
    return api.info.title + '-' + api.info.version;
  } else {
    return null;
  }
};

export const generateFileName = (name = 'file', type = 'yaml') => {
  if (name && type) {
    return `${name.replace(/ /g, '_')}.${type}`;
  }
  return null;
};

export const createObjectBlob = (object, fileName, removeIBMKeys = false, type = 'yaml') => {
  if (object && fileName) {
    let newFileContentState = {};

    newFileContentState.filename = generateFileName(fileName, type);

    let fileout = Object.assign({}, object);
    delete fileout['x-embedded-doc'];

    // remove x-ibm attributes
    if (removeIBMKeys) {
      removeIBMAttributes(fileout);
    }

    let file;

    if (!type || type === 'yaml') {
      file = jsyaml.dump(fileout, { lineWidth: -1 });
    } else {
      try {
        file = JSON.stringify(fileout, null, 2);
      } catch (e) {
        console.error(e);
      }
    }

    if (!file) return {};

    if (isWindows()) {
      file = file.replace(/\n/g, '\r\n');
    }

    let blob = new Blob([file], { type: 'text/plain;charset=utf-8;' });
    newFileContentState.fileContent = isIE()
      ? // when browser is IE
        // it uses msSaveBlob function below which packages the `blob`
        blob
      : // non-IE browsers
        URL.createObjectURL(blob);

    return newFileContentState;
  } else {
    return {};
  }
};

export const removeIBMAttributes = (obj = {}) => {
  Object.keys(obj).forEach(function (key) {
    if (key.startsWith('x-ibm')) {
      delete obj[key];
    } else if (typeof obj[key] === 'object' && !Object.is(obj[key], null)) {
      removeIBMAttributes(obj[key]);
    }
  });
  return obj;
};

export const removeParserKeys = (obj = {}) => {
  return Object.keys(obj)
    .filter((key) => !key.match(/x-parser-.*/g))
    .reduce((acc, key) => {
      let newObj = obj[key];
      if (typeof newObj === 'object') {
        newObj = removeParserKeys(obj[key]);
      }
      acc[key] = newObj;
      return acc;
    }, {});
};

export const findFirstAppWithAccess = (apps) => {
  let result;
  if (apps) {
    result = Object.values(apps).find((app) => app.subscribed === true);
  }
  return result;
};

export const handleKeyDown = (func) => (event) => {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault(); // Prevents default space bar scrolling behavior
    func();
  }
};
